import React, { useEffect, useState, useMemo } from "react";
import { EnhancedPresence } from "./EnhancedPresence";
import { useCollaborationStore } from "@/stores/collaboration/collaboration-store";
import { useAuthStore } from "@/stores/auth/auth-store";
import { socketClient } from "@/services/websocket/socketClient";

// Mapping from collaboration store types to EnhancedPresence types
interface UserPresenceData {
  userId: string;
  username: string;
  avatar?: string;
  status: "online" | "away" | "busy" | "idle" | "offline";
  activity: {
    currentAction:
      | "viewing"
      | "editing"
      | "selecting"
      | "dragging"
      | "commenting"
      | "annotating"
      | "typing"
      | "painting"
      | "drawing"
      | "measuring"
      | "presenting"
      | "reviewing"
      | "debugging"
      | "idle";
    targetComponentId?: string;
    isCollaborative: boolean;
    collaborators?: string[];
    startedAt: Date;
  };
  location: {
    viewport: {
      x: number;
      y: number;
      zoom: number;
      width: number;
      height: number;
    };
    cursor: {
      x: number;
      y: number;
      componentId?: string;
    };
    focusedComponent?: string;
    selectedComponents: string[];
    currentLayer: number;
  };
  devices: {
    type: "desktop" | "tablet" | "mobile" | "tv";
    browser: string;
    os: string;
    touchEnabled: boolean;
  }[];
  joinedAt: Date;
  lastActiveAt: Date;
  totalSessionTime: number;
  isIdle: boolean;
  permissions: {
    canEdit: boolean;
    canComment: boolean;
    canDelete: boolean;
    canShare: boolean;
    canAdmin: boolean;
  };
  preferences: {
    showActivity: boolean;
    showLocation: boolean;
    privacyMode: "full" | "limited" | "private";
  };
}

interface PresenceAdapterProps {
  projectId?: string;
  showDetailedInfo?: boolean;
  compactMode?: boolean;
  onUserClick?: (userId: string) => void;
}

interface CursorUpdateEvent {
  userId: string;
  payload: {
    position: {
      x: number;
      y: number;
      componentId?: string;
    };
  };
}

interface PresenceUpdateEvent {
  userId: string;
  payload: {
    action: string;
  };
}

interface UserJoinedEvent {
  userId: string;
}

interface UserLeftEvent {
  userId: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const isCursorUpdateEvent = (value: unknown): value is CursorUpdateEvent => {
  if (!isRecord(value) || typeof value.userId !== "string") {
    return false;
  }

  const { payload } = value;
  if (!isRecord(payload) || !isRecord(payload.position)) {
    return false;
  }

  const { position } = payload;
  return (
    isFiniteNumber(position.x) &&
    isFiniteNumber(position.y) &&
    (position.componentId === undefined ||
      typeof position.componentId === "string")
  );
};

const isPresenceUpdateEvent = (
  value: unknown,
): value is PresenceUpdateEvent => {
  if (!isRecord(value) || typeof value.userId !== "string") {
    return false;
  }

  const { payload } = value;
  return isRecord(payload) && typeof payload.action === "string";
};

const isUserJoinedEvent = (value: unknown): value is UserJoinedEvent =>
  isRecord(value) && typeof value.userId === "string";

const isUserLeftEvent = (value: unknown): value is UserLeftEvent =>
  isRecord(value) && typeof value.userId === "string";

export const PresenceAdapter: React.FC<PresenceAdapterProps> = ({
  projectId,
  showDetailedInfo = true,
  compactMode = false,
  onUserClick,
}) => {
  const { user: currentUser } = useAuthStore();
  const {
    activeUsers,
    userPresence,
    dragConflicts,
    componentLocks,
    isActive: isCollaborationActive,
    sessionId,
  } = useCollaborationStore();

  const [cursorPositions, setCursorPositions] = useState<
    Map<string, { x: number; y: number; componentId?: string }>
  >(new Map());
  const [userViewports, setUserViewports] = useState<
    Map<
      string,
      { x: number; y: number; zoom: number; width: number; height: number }
    >
  >(new Map());
  const [activityStates, setActivityStates] = useState<Map<string, string>>(
    new Map(),
  );

  // Set up real-time WebSocket listeners for presence data
  useEffect(() => {
    if (!isCollaborationActive || !socketClient.isConnected()) return;

    // Listen for cursor updates
    const handleCursorUpdate = (data: unknown) => {
      if (isCursorUpdateEvent(data) && data.userId !== currentUser?.id) {
        setCursorPositions((prev) =>
          new Map(prev).set(data.userId, {
            x: data.payload.position.x,
            y: data.payload.position.y,
            componentId: data.payload.position.componentId,
          }),
        );
      }
    };

    // Listen for presence updates
    const handlePresenceUpdate = (data: unknown) => {
      if (isPresenceUpdateEvent(data) && data.userId !== currentUser?.id) {
        const action = data.payload.action;
        setActivityStates((prev) => new Map(prev).set(data.userId, action));
      }
    };

    // Listen for user join/leave events
    const handleUserJoined = (data: unknown) => {
      if (!isUserJoinedEvent(data) || data.userId === currentUser?.id) {
        return;
      }
    };

    const handleUserLeft = (data: unknown) => {
      if (!isUserLeftEvent(data)) {
        return;
      }

      setCursorPositions((prev) => {
        const newMap = new Map(prev);
        newMap.delete(data.userId);
        return newMap;
      });
      setActivityStates((prev) => {
        const newMap = new Map(prev);
        newMap.delete(data.userId);
        return newMap;
      });
    };

    // Register event handlers
    const unsubscribers = [
      socketClient.on("cursorUpdate", handleCursorUpdate),
      socketClient.on("presenceUpdate", handlePresenceUpdate),
      socketClient.on("userJoined", handleUserJoined),
      socketClient.on("userLeft", handleUserLeft),
    ];

    return () => {
      // Cleanup event handlers
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [isCollaborationActive, currentUser?.id]);

  // Transform collaboration store data to EnhancedPresence format
  const presenceUsers: UserPresenceData[] = useMemo(() => {
    const users: UserPresenceData[] = [];

    // Convert active users from collaboration store
    activeUsers.forEach((userSession) => {
      const presence = userPresence.get(userSession.userId) || "offline";
      const cursor = cursorPositions.get(userSession.userId);
      const viewport = userViewports.get(userSession.userId);
      const currentActivity = activityStates.get(userSession.userId);

      // Determine activity based on current state
      let activityType: UserPresenceData["activity"]["currentAction"] = "idle";
      let isCollaborative = false;
      let collaborators: string[] = [];

      // Check for drag conflicts
      const userConflicts = Array.from(dragConflicts.values()).filter(
        (conflict) =>
          conflict.users.some((u) => u.userId === userSession.userId),
      );

      if (userConflicts.length > 0) {
        isCollaborative = true;
        collaborators = userConflicts
          .flatMap((c) => c.users.map((u) => u.userId))
          .filter((id) => id !== userSession.userId);
      }

      // Map current action to activity type
      if (userSession.currentAction === "dragging") {
        activityType = "dragging";
      } else if (userSession.currentAction === "editing") {
        activityType = "editing";
      } else if (userSession.selection && userSession.selection.length > 0) {
        activityType = "selecting";
      } else if (currentActivity === "commenting") {
        activityType = "commenting";
      } else {
        activityType = "viewing";
      }

      const user: UserPresenceData = {
        userId: userSession.userId,
        username: userSession.user.username || userSession.user.email,
        avatar: userSession.user.avatar,
        status:
          presence === "online"
            ? "online"
            : presence === "idle"
              ? "idle"
              : presence === "offline"
                ? "offline"
                : "online",
        activity: {
          currentAction: activityType,
          targetComponentId:
            userSession.draggedComponent || userSession.selection?.[0],
          isCollaborative,
          collaborators: collaborators.length > 0 ? collaborators : undefined,
          startedAt: userSession.lastActivity,
        },
        location: {
          viewport: viewport || {
            x: 0,
            y: 0,
            zoom: 1,
            width: 1920,
            height: 1080,
          },
          cursor: cursor || {
            x: 0,
            y: 0,
            componentId: userSession.draggedComponent,
          },
          focusedComponent: userSession.draggedComponent,
          selectedComponents: userSession.selection || [],
          currentLayer: 0,
        },
        devices: [
          {
            type: "desktop", // Default to desktop, could be enhanced with real device detection
            browser: "Chrome", // Could be detected from user agent
            os: "Unknown",
            touchEnabled: false,
          },
        ],
        joinedAt: new Date(Date.now() - 30000), // Approximate, could be tracked more accurately
        lastActiveAt: userSession.lastActivity,
        totalSessionTime: Date.now() - userSession.lastActivity.getTime(),
        isIdle: presence === "idle",
        permissions: {
          canEdit: true, // Could be based on project permissions
          canComment: true,
          canDelete: true,
          canShare: false,
          canAdmin: false,
        },
        preferences: {
          showActivity: true,
          showLocation: true,
          privacyMode: "full",
        },
      };

      users.push(user);
    });

    return users;
  }, [
    activeUsers,
    userPresence,
    cursorPositions,
    userViewports,
    activityStates,
    dragConflicts,
  ]);

  // Don't render if collaboration is not active
  if (!isCollaborationActive || !currentUser) {
    return null;
  }

  return (
    <EnhancedPresence
      users={presenceUsers}
      currentUserId={currentUser.id}
      onUserClick={onUserClick}
      showDetailedInfo={showDetailedInfo}
      compactMode={compactMode}
    />
  );
};

export default PresenceAdapter;
